// @ts-strict-ignore
import axios from "axios";
import { BASE_URL, EXCHANGE_BASE_URL } from "config";
import dayjs from "dayjs";
import type { Translate } from "i18n/types";
import { logger } from "observability";
import type { Range } from "types/prices";
import queryStringPkg from "query-string";
import type { ASSET_PRICE } from "types/converter";
import { captureException, captureMessage } from "observability";
const buildPriceEndpoint = (timeframe: string): string => {
  switch (timeframe) {
    case "1h":
      return "1m?skip=1";
    case "1d":
      return "1m?skip=6";
    case "1m":
      return "30m?skip=4";
    case "1w":
      return "5m?skip=6";
    case "3m":
      return "6hr?skip=3";
    case "1y":
      return "1day?skip=1";
    default:
      return "1m?skip=6";
  }
};

export const fetchAssetPrice = (
  symbol: string,
  range: string,
  fiatSymbolParam?: string
) => {
  const fiatSymbol = fiatSymbolParam ?? "USD";
  return axios.get<ASSET_PRICE>(
    `${EXCHANGE_BASE_URL}/web/details/${symbol}${fiatSymbol}/${buildPriceEndpoint(range)}`,
    {
      headers: { "Content-Type": "application/json" }
    }
  );
};

export type PriceData = {
  symbol: PriceDataSymbol;
  marketCap: {
    currency: string;
    value: string;
  } | null;
  supply: {
    currency: string;
    value: string;
  } | null;
};

type PriceDataSymbol = string; // eg. LOOMUSD
export type ExchangePriceData = PriceData & {
  from: string;
  to: string;
  fromDisplayName: string;
  toDisplayName: string;
  "24hrMin": string;
  "24hrMax": string;
  "24hrDelta": string;
  "24hrPercentDelta": string;
  rangeDelta: string;
  rangePercentDelta: string;
  // Range is really big. Has around a thousand data points
  range: Range[];
  bid: string;
  ask: string;
  marketCap: {
    currency: string;
    value: string;
  } | null;
  supply: {
    currency: string;
    value: string;
  } | null;
  allTimeHigh: {
    currency: string;
    value: string;
  };
  volume: {
    currency: string;
    value: string;
  };
  instantMarketOpen: boolean;
  limitOrderOpen: boolean;
  stopLimitOrderOpen: boolean;
  marketOrderOpen: boolean;
  priceInputDecimals: number;
  isSyntheticPair: boolean;
  isPerpPair: boolean;
  lastTradePrice: string;
};

export const fetchTokenizedStocksPrices = () =>
  axios.get<ExchangePricesResponse>(
    `https://exchange.gemini.com/tokenized-stocks/market/public`, // Don't need the range values
    {
      headers: { "Content-Type": "application/json" }
    }
  );

export const fetchTokenizedStockDetail = (
  symbol: string,
  interval: string,
  fiatCurrency: string = "USD"
) =>
  axios.get<ExchangePriceData>(
    `https://exchange.gemini.com/tokenized-stocks/details/public/${symbol}/${interval}?fiatCurrency=${fiatCurrency}`,
    {
      headers: { "Content-Type": "application/json" }
    }
  );

type ExchangePricesResponse = ExchangePriceData[];

export const fetchExchangePrices = (filter: string) =>
  axios.get<ExchangePricesResponse>(
    `${EXCHANGE_BASE_URL}/alldetails/${buildPriceEndpoint(filter)}`, // Don't need the range values
    {
      headers: { "Content-Type": "application/json" }
    }
  );

export type ExternalPriceData = PriceData & {
  range: Range[];
  isTrading: boolean;
  isCustody: boolean;
};

type ExternalPricesResponse = ExternalPriceData[];

export const fetchExternalPrices = () =>
  axios.get<ExternalPricesResponse>(
    `${EXCHANGE_BASE_URL}/allPublicDetailsExternalDaily`,
    {
      headers: { "Content-Type": "application/json" }
    }
  );

// Adapted from /api/all-prices/[filter].ts
// Returns prices from exchange and external sources
export type AllPricesResponse = Awaited<ReturnType<typeof fetchAllPrices>>;

export const fetchAllPrices = async (timeframe: string) => {
  const [exchangeRes, externalRes] = await Promise.all([
    fetchExchangePrices(timeframe),
    fetchExternalPrices()
  ]);

  // Convert array of objects to object with symbol as key
  const exchangeData = exchangeRes.data.reduce(
    (data, current) => ({
      ...data,
      [String(current.symbol).toUpperCase()]: current
    }),
    {} as Record<PriceDataSymbol, ExchangePriceData>
  );

  const externalData = externalRes.data.reduce(
    (data, current) => ({
      ...data,
      [String(current.symbol).toUpperCase()]: current
    }),
    {} as Record<PriceDataSymbol, ExternalPriceData>
  );

  const cachedCoinInfo = (await import("../../_data/coin-info.json")).default;

  const prices = cachedCoinInfo.items.map((coin) => {
    return {
      ...(coin.fields.exchangePrices
        ? exchangeData[`${String(coin.fields.coin).toUpperCase()}USD`]
        : externalData[`${String(coin.fields.coin).toUpperCase()}USD`]),
      ...coin.fields
    };
  });

  // Hardcode GUSD values when attempting to pull from external
  const gusdPrice = prices.find((p) => p.coin === "GUSD" && !p.exchangePrices);

  if (gusdPrice) {
    Object.assign(gusdPrice, {
      ask: "1",
      bid: "1",
      range: [
        { x: Number(dayjs().subtract(365, "day")), y: 1.0 },
        { x: Number(dayjs()), y: 1.0 }
      ]
    });
  }

  return prices;
};

const MARKET_FILTER_API_BASE_URL = "https://mobile.gemini.com/mobile/market";

export type MarketFilter = {
  name: string;
  description: string;
  assets: string[];
  type:
    | "AllMarketFilter"
    | "WatchlistMarketFilter"
    | "TopMoversMarketFilter"
    | "TopTradedMarketFilter"
    | "RecentlyLaunchedMarketFilter"
    | "MemeMarketFilter"
    | "MetaverseMarketFilter"
    | "DeFiMarketFilter"
    | "StablecoinsMarketFilter"
    | "TokenizedStocksMarketFilter"
    | "AllCryptocurrenciesMarketFilter";
};

type MarketAsset = {
  symbol: string;
  from: string;
  to: string;
  fromDisplayName: string;
  toDisplayName: string;
  "24hrMin": string;
  "24hrMax": string;
  "24hrDelta": string;
  "24hrPercentDelta": string;
  rangeDelta: string;
  rangePercentDelta: string;
  range: null;
  bid: string;
  ask: string;
  marketCap: {
    currency: string;
    value: string;
  };
  supply: {
    currency: string;
    value: string;
  };
  allTimeHigh: {
    currency: string;
    value: string;
  };
  volume: {
    currency: string;
    value: string;
  };
  instantMarketOpen: boolean;
  limitOrderOpen: boolean;
  stopLimitOrderOpen: boolean;
  marketOrderOpen: boolean;
  priceInputDecimals: number;
  isSyntheticPair: boolean;
  isPerpPair: boolean;
  lastTradePrice: string;
};

type MarketFilterResponse = {
  filters: MarketFilter[];
  assets: MarketAsset[];
};

export const fetchMarketFilters = async (priceCurrency?: string) => {
  try {
    const response = await fetch(
      `${MARKET_FILTER_API_BASE_URL}?skip=6&priceCurrency=${priceCurrency ?? "USD"}&includeRange=false`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const { filters, assets } = (await response.json()) as MarketFilterResponse;
    return { filters, assets };
  } catch (error) {
    logger.error("An error occurred while fetching market filters:", error);
  }
  return;
};

export const generateTimeFrameString = (
  timeframe: string,
  t: Translate<"prices">
): string => {
  const timeFrames: Record<string, string> = {
    "1h": t("timeFrame1h"),
    "1d": t("timeFrame1d"),
    "1w": t("timeFrame1w"),
    "1m": t("timeFrame1m"),
    "1y": t("timeFrame1y"),
    all: t("allTime")
  };

  return timeFrames[timeframe] || "---";
};

export const generateOGParamsForCoin = (
  paramsObj: Record<string, any>,
  skipKeys?: Array<string>
) => {
  const price = paramsObj.range?.[paramsObj.range.length - 1]?.y;
  const timestamp = paramsObj.range?.[paramsObj.range.length - 1]?.x;

  const queryStringObject = {
    // 'cn' might not be needed as the
    // path param already contains the coin name
    // and hence we are avoiding it conditionally
    ...(!skipKeys?.includes("cn")
      ? { cn: paramsObj.toDisplayName ?? "NA" }
      : { cn: undefined }),
    // other properties
    cs: paramsObj.to ?? "NA",
    pr: price ?? "NA",
    pc: paramsObj.rangePercentDelta ?? "NA",
    ts: timestamp ?? "NA"
  };

  return queryStringObject;
};

export async function generateOGImageBase64ForCoin(
  paramsObj: Record<string, any>
) {
  try {
    const response = await fetch(
      `${BASE_URL}/og/coinprice?${queryStringPkg.stringify(generateOGParamsForCoin(paramsObj))}`
    );
    if (response.ok) {
      const imageBlob = await response.blob();
      const imageBuffer = await imageBlob.arrayBuffer();
      const base64Image = btoa(
        new Uint8Array(imageBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );
      const imageURL = `data:image/png;base64,${base64Image}`;
      return imageURL;
    } else {
      captureMessage(
        `Failed to fetch the image. HTTP status:${response.status}`
      );
    }
  } catch (error) {
    captureException(error as Error);
  }

  return ""; // Return '' in case of an error or failure
}

export const generateBase64HashForCoin = ({
  coinSymbol,
  coinPrice,
  percentageChange,
  timestamp,
  coinName
}: {
  coinName: string;
  coinSymbol: string;
  coinPrice: string;
  percentageChange: string;
  timestamp: string;
}) => {
  return btoa(
    `${coinName}_${coinSymbol}_${coinPrice}_${percentageChange}_${timestamp}`
  );
};

export const decodeBase64HashForCoin = (
  hashCode: string
): Record<string, string> => {
  try {
    const decodedHash = atob(hashCode);
    const [coinName, coinSymbol, price, percentageChange, timestamp] =
      decodedHash.split("_");
    const finalCoinData = {};
    if (coinSymbol && price && percentageChange && timestamp) {
      Object.assign(finalCoinData, {
        coinSymbol,
        price,
        percentageChange,
        timestamp,
        coinName
      });
    }
    return finalCoinData;
  } catch (e) {
    captureException(e as Error);
    return {};
  }
};
